import { useMutation } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { completePayment, validateWalletMerchant } from '@/api/payments';
import { LoadingScreen } from '@/features/check/StatusScreen';
import { PaymentActionHost } from './PaymentActionHost';
import { PaymentFailureScreen } from './PaymentFailureScreen';
import { ReceiptScreen } from '@/features/receipt/ReceiptScreen';
import type { PaymentStep } from './paymentStep';
import type { Check, Location, PaymentFailure } from '@/api/types';

/** Anything the server did not describe well enough to act on. Retryable: it may just be a bad moment. */
const UNREADABLE: PaymentFailure = { code: 'gatewayUnavailable', retryable: true };

interface PaymentRunnerProps {
    location: Location;
    check: Check;
    accessToken: string;
    paymentId: string;
    /** Where this run starts: an action from `POST /payments`, or a result recovered after a redirect. */
    initial: PaymentStep;
    onRetry: () => void;
    onBackToCheck: () => void;
}

/**
 * Drives a payment from wherever it currently is to an outcome.
 *
 * Obey the action, hand what comes back to `/complete`, look at the answer — and if the answer is another
 * action, go round again. That loop is the entire payment implementation, including 3-D Secure: a challenge
 * arrives as a `requiresAction` and is obeyed by the same code that obeyed the first instruction.
 *
 * Shared by the two ways a payment can be in flight — one starting here, one resumed after a hosted page
 * sent the browser back — so the two cannot drift into disagreeing about what a result means.
 *
 * Anything it cannot make sense of becomes a failure rather than a blank screen: a success with no receipt,
 * a `requiresAction` with nothing to do, an action this build cannot obey. A guest is better told it did not
 * work than shown a screen assembled from a result the server never sent.
 */
export function PaymentRunner({
    location,
    check,
    accessToken,
    paymentId,
    initial,
    onRetry,
    onBackToCheck,
}: PaymentRunnerProps) {
    const [step, setStep] = useState<PaymentStep>(initial);
    const [unsupported, setUnsupported] = useState<{ detail?: string } | null>(null);

    const complete = useMutation({
        mutationFn: (result: Record<string, unknown>) => completePayment(accessToken, paymentId, result),
        // Whatever comes back is an outcome. There is no second instruction to obey - see PaymentStep.
        onSuccess: (result) => setStep({ kind: 'result', result }),
    });

    /*
     * Reports whether the server settled it, which Apple Pay needs before it can dismiss its own sheet.
     *
     * `mutateAsync` rather than `mutate` because the answer has to reach the caller and not only land in
     * state. Its rejection is caught here so a failed call reads as "not settled" rather than escaping as an
     * unhandled promise — the failure is already on screen via `complete.isError`.
     */
    const onResult = useCallback(
        async (result: Record<string, unknown>) => {
            try {
                const settled = await complete.mutateAsync(result);
                return settled.status === 'succeeded';
            } catch {
                return false;
            }
        },
        [complete],
    );

    /**
     * Apple hands the browser a validation URL that only our server holds the certificate to answer.
     *
     * Not scoped to the payment, and does not need to be: the same call answers whether the guest is paying
     * two dollars or two hundred. The location comes off the session token.
     */
    const onValidateMerchant = useCallback(
        (validationUrl: string) => validateWalletMerchant(accessToken, validationUrl),
        [accessToken],
    );

    const onUnsupported = useCallback((detail?: string) => setUnsupported({ detail }), []);

    const failed = (failure: PaymentFailure, detail?: string) => (
        <PaymentFailureScreen
            location={location}
            check={check}
            failure={failure}
            detail={detail}
            onRetry={onRetry}
            onBackToCheck={onBackToCheck}
        />
    );

    if (unsupported || complete.isError) return failed(UNREADABLE, unsupported?.detail);
    if (complete.isPending) return <LoadingScreen />;

    if (step.kind === 'action') {
        return (
            <PaymentActionHost
                action={step.action}
                location={location}
                onResult={onResult}
                onValidateMerchant={onValidateMerchant}
                onUnsupported={onUnsupported}
                onCancelled={onRetry}
            />
        );
    }

    const { result } = step;

    if (result.status === 'pending') return <LoadingScreen />;

    if (result.status === 'succeeded' && result.receipt) {
        return <ReceiptScreen location={location} check={check} receipt={result.receipt} accessToken={accessToken} />;
    }

    return failed(result.failure ?? UNREADABLE);
}
